import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import {
  fetchContentItem,
  fetchContentList,
  fetchPageBlocks,
  resolveSlugRedirect,
} from "./cms.server";
import { kindSchema, localeSchema, requestSchema } from "./cms.schemas";


export const listContent = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) =>
    z.object({ kind: kindSchema, locale: localeSchema, limit: z.number().int().min(1).max(100).optional() }).parse(data),
  )
  .handler(async ({ data }) => fetchContentList(data.kind, data.locale, data.limit ?? 60));

export const getContent = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) =>
    z.object({ kind: kindSchema, slug: z.string().min(1).max(200), locale: localeSchema }).parse(data),
  )
  .handler(async ({ data }) => fetchContentItem(data.kind, data.slug, data.locale));

export const getSlugRedirect = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) =>
    z.object({ kind: kindSchema, slug: z.string().min(1).max(300), locale: localeSchema }).parse(data),
  )
  .handler(async ({ data }) => resolveSlugRedirect(data.kind, data.slug, data.locale));

export const getPageBlocks = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) =>
    z.object({ pageSlug: z.string().min(1).max(120), locale: localeSchema }).parse(data),
  )
  .handler(async ({ data }) => fetchPageBlocks(data.pageSlug, data.locale));


const ALLOWED_ORIGIN = /^https:\/\/((www\.)?factory\.online|[a-z0-9-]+\.lovable\.app|[a-z0-9-]+\.lovableproject\.com)$|^http:\/\/localhost(:\d+)?$/;
const RATE = new Map<string, number[]>();
const GENERIC_ERROR = "We could not submit your request right now. Please try again.";

function rateLimited(ip: string): boolean {
  const now = Date.now();
  const hits = (RATE.get(ip) ?? []).filter((t) => now - t < 10 * 60_000);
  hits.push(now);
  RATE.set(ip, hits);
  if (RATE.size > 5000) RATE.clear();
  return hits.length > 8;
}

export const submitRequest = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => requestSchema.parse(data))
  .handler(async ({ data }) => {
    if (data.website) return { ok: true as const };
    const requestId = `req_${crypto.randomUUID().replace(/-/g, "").slice(0, 16)}`;

    const { getRequestHeader } = await import("@tanstack/react-start/server");
    const origin = getRequestHeader("origin");
    if (origin && !ALLOWED_ORIGIN.test(origin)) throw new Error(GENERIC_ERROR);
    const ip = getRequestHeader("cf-connecting-ip") ?? getRequestHeader("x-forwarded-for")?.split(",")[0]?.trim() ?? "unknown";
    if (rateLimited(ip)) throw new Error("Too many requests. Please try again in a few minutes.");

    // Resolve the real, region-specific price for the site locale — never trust the client-sent price line.
    let message = (data.message || "").replace(/^Recommended plan:.*$/gm, "").replace(/\n{3,}/g, "\n\n").trim();
    let planLine: string | undefined;
    if (data.planSlug) {
      const { fetchPricing } = await import("./pricing.server");
      const pricing = await fetchPricing(data.locale);
      const plan = pricing.plans.find((item) => item.slug === data.planSlug);
      if (plan) {
        planLine = `Recommended plan: ${plan.name} — ${plan.price} ${plan.period} (${data.locale} pricing)`.trim();
        message = message ? `${message}\n${planLine}` : planLine;
      }
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { COUNTRY_BY_CODE } = await import("./countries");
    const countryName = COUNTRY_BY_CODE[data.country] ?? data.country;

    // One row per submission id: retries of the same submission do not duplicate the row or notification.
    const { data: inserted, error } = await supabaseAdmin
      .from("contact_requests")
      .upsert(
        {
          submission_id: data.submissionId,
          kind: data.kind,
          full_name: data.fullName,
          email: data.email,
          company: data.company || null,
          phone: data.phone || null,
          country: data.country,
          industry: data.industry,
          message: message || null,
          locale: data.locale,
          crm_status: "pending",
        },
        { onConflict: "submission_id", ignoreDuplicates: true },
      )
      .select("id");

    if (error) {
      console.error(`[submitRequest] req=${requestId} db_error`);
      throw new Error(GENERIC_ERROR);
    }

    if (inserted && inserted.length) {
      const { notifyTelegram } = await import("./telegram.server");
      await notifyTelegram({
        kind: data.kind,
        fullName: data.fullName,
        email: data.email,
        company: data.company || undefined,
        phone: data.phone || undefined,
        country: countryName,
        industry: data.industry,
        message: message || undefined,
        plan: planLine ?? (data.planSlug || undefined),
        pagePath: data.pagePath || undefined,
        locale: data.locale,
      });
    }

    const { sendCrmLead } = await import("./bhag-crm.server");
    const crm = await sendCrmLead(
      {
        submissionId: data.submissionId,
        kind: data.kind,
        fullName: data.fullName,
        email: data.email,
        phone: data.phone,
        company: data.company,
        industry: data.industry,
        country: countryName,
        message: data.message,
        pageUrl: data.pageUrl,
        referrer: data.referrer,
        utmSource: data.utmSource,
        utmMedium: data.utmMedium,
        utmCampaign: data.utmCampaign,
        utmTerm: data.utmTerm,
        utmContent: data.utmContent,
      },
      { requestId },
    );

    await supabaseAdmin
      .from("contact_requests")
      .update({
        crm_status: crm.ok ? (crm.replayed ? "replayed" : "created") : `failed:${crm.reason}`,
        crm_ingestion_id: crm.ok ? (crm.ingestionId ?? null) : null,
      })
      .eq("submission_id", data.submissionId);

    if (!crm.ok) {
      console.error(`[submitRequest] req=${requestId} crm_failed reason=${crm.reason}`);
      if (crm.reason === "validation" && crm.fields?.length) {
        throw new Error(`Please check these fields: ${crm.fields.join(", ")}.`);
      }
      // Lead is already saved and the team notified, so a config/auth problem never loses the request.
      if (crm.reason === "config" || crm.reason === "auth" || crm.reason === "conflict") {
        return { ok: true as const, requestId };
      }
      throw new Error(`${GENERIC_ERROR} (${requestId})`);
    }

    return { ok: true as const, ingestionId: crm.ingestionId };
  });

/** Public read: products linked to an industry item. */
export const getIndustryProducts = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) =>
    z.object({ itemId: z.string().uuid(), locale: localeSchema.optional() }).parse(data),
  )
  .handler(async ({ data }) => {
    const { fetchIndustryProducts } = await import("./cms.server");
    return fetchIndustryProducts(data.itemId, data.locale ?? "en");
  });

export const listBlogCategories = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => z.object({ locale: localeSchema }).parse(data))
  .handler(async ({ data }) => {
    const { fetchBlogCategories } = await import("./cms.server");
    return fetchBlogCategories(data.locale);
  });
